import 'toastify-js/src/toastify.css';
import Toastify from 'toastify-js';
import type { ToastOptions } from '@/types';

/**
 * Toast notification composable
 * 提供简单的消息提示功能
 */
export function useToast() {
  // 默认配置
  const defaultOptions = {
    duration: 3000,
    close: true,
    gravity: 'top' as const,
    position: 'right' as const,
    stopOnFocus: true,
  };

  /**
   * 显示一个消息提示
   */
  const showToast = (options: ToastOptions | string, type?: 'success' | 'error' | 'warning' | 'info', customOptions?: any): void => {
    let message: string;
    let toastType: 'success' | 'error' | 'warning' | 'info';
    let extraOptions: any = {};

    if (typeof options === 'string') {
      message = options;
      toastType = type || 'info';
      extraOptions = customOptions || {};
    } else {
      message = options.message;
      toastType = options.type;
      extraOptions = {
        duration: options.duration,
        showClose: options.showClose
      };
    }

    const backgroundColor = getBackgroundColor(toastType);
    
    Toastify({
      ...defaultOptions,
      ...extraOptions,
      text: message,
      className: `toast-${toastType}`,
      style: {
        background: backgroundColor,
        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16)',
        borderRadius: '4px',
      }
    }).showToast();
  };

  /**
   * 根据类型获取背景颜色
   */
  const getBackgroundColor = (type: string): string => {
    switch (type) {
      case 'success':
        return 'linear-gradient(to right, #00b09b, #96c93d)';
      case 'error':
        return 'linear-gradient(to right, #ff5f6d, #ffc371)';
      case 'warning':
        return 'linear-gradient(to right, #f7b733, #fc4a1a)';
      case 'info':
      default:
        return 'linear-gradient(to right, #2193b0, #6dd5ed)';
    }
  };

  /**
   * 显示成功提示
   */
  const showSuccess = (message: string, options: any = {}): void => {
    showToast(message, 'success', options);
  };

  /**
   * 显示错误提示
   */
  const showError = (message: string, options: any = {}): void => {
    showToast(message, 'error', options);
  };

  /**
   * 显示警告提示
   */
  const showWarning = (message: string, options: any = {}): void => {
    showToast(message, 'warning', options);
  };

  /**
   * 显示信息提示
   */
  const showInfo = (message: string, options: any = {}): void => {
    showToast(message, 'info', options);
  };

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
}
